import { useState, useEffect, useRef, useCallback } from 'react';
import { Equipment, EquipmentType, EQUIPMENT_TYPES } from '../types/project';
import { Plus, Trash2, Info, X } from 'lucide-react';
import { Tooltip } from './Tooltip';
import type { RecentEquipment } from '../lib/recentEquipment';
import {
  getConcurrencyState,
  getAutoFillValues,
  isAutoFillable,
} from '../lib/concurrency';

interface EquipmentScheduleProps {
  equipment: Equipment[];
  /** A seed makes the new row start as that equipment (the quick-add chips). */
  onAdd: (seed?: Partial<Equipment>) => void;
  /** The contractor's own recently entered loads, newest first. */
  recent?: RecentEquipment[];
  /** Takes a one-off off the chips; it returns if entered again. */
  onRemoveRecent?: (key: string) => void;
  onUpdate: (id: string, updates: Partial<Equipment>) => void;
  onDelete: (id: string) => void;
  isReadOnly?: boolean;
  peakSeason?: string;
  /** The measured method's section for this project's code edition — 220.87,
   * or 120.87 under the 2026 Article 120 reorg. Passed in because this
   * component holds equipment, not the project. */
  measuredCitation?: string;
  /**
   * 'removed' renders the same rows as equipment being taken out with this
   * work (load_role 'removed'): its own heading, test ids prefixed
   * `removed-`, no Add again chips, and "running at peak" wording. The season
   * determination is the same one new loads use — it decides the 2026 credit.
   */
  variant?: 'new' | 'removed';
  /** Removed variant: how removed equipment is treated on this project. */
  removedNote?: string;
  /** Removed variant: put a row back in the dwelling inventory. */
  onReturnToInventory?: (id: string) => void;
  /** 2026 projects: ask on each new Heat Pump row whether its rating includes
   * backup strip heat (loadRoles.ts countsAsCentralResistanceHeat). */
  askBackupHeat?: boolean;
}

export function EquipmentSchedule({
  equipment,
  onAdd,
  onUpdate,
  onDelete,
  isReadOnly = false,
  peakSeason,
  measuredCitation = '220.87',
  recent = [],
  onRemoveRecent,
  variant = 'new',
  removedNote,
  onReturnToInventory,
  askBackupHeat = false,
}: EquipmentScheduleProps) {
  const isRemoved = variant === 'removed';
  // Two instances share one page; the removed list's ids must not collide
  // with the New Loads ids the harness drives.
  const tid = (id: string) => (isRemoved ? `removed-${id}` : id);

  // The backup-heat question, shared by both layouts. Only new heat pumps on a
  // 2026 project: Table 120.83 puts central resistance heat at 80% and other
  // new loads at 50%, and strips folded into a heat pump's rating cannot be
  // separated by the engine. Unanswered is counted as included.
  const backupHeatQuestion = (eq: Equipment, index: number, compact: boolean) =>
    !isRemoved && askBackupHeat && eq.equipment_type === 'Heat Pump' ? (
      <label className={`block ${compact ? 'mt-1 text-xs' : 'mt-2 text-sm'} text-gray-700`}>
        <span className="flex items-center gap-1">
          Backup strip heat in this rating?
          <Tooltip text="NEC 2026 Table 120.83 counts new central electric resistance heat at 80% and other new loads, heat pumps included, at 50%. If the rating you entered includes the air handler's backup strips, the whole row counts at 80%. Better still, enter the strips as their own Strip Heat row and answer No here. Until you answer, TrueLoad counts the row at 80%." />
        </span>
        <select
          value={eq.includes_backup_heat == null ? '' : eq.includes_backup_heat ? 'yes' : 'no'}
          onChange={(e) =>
            onUpdate(eq.id, {
              includes_backup_heat: e.target.value === '' ? null : e.target.value === 'yes',
            })
          }
          data-testid={tid(`equipment-backup-heat-${index}`)}
          className={`mt-1 w-full border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 ${
            eq.includes_backup_heat == null ? 'border-amber-400' : 'border-gray-300'
          } ${compact ? 'px-2 py-1' : 'px-3 py-2.5 text-base'}`}
          disabled={isReadOnly}
        >
          <option value="">Not answered (counted at 80%)</option>
          <option value="no">No: compressor only (50%)</option>
          <option value="yes">Yes: includes strips (80%)</option>
        </select>
      </label>
    ) : null;
  const [overrideMode, setOverrideMode] = useState<Set<string>>(new Set());
  const prevSeasonRef = useRef<string | undefined>(undefined);
  const prevEquipmentLengthRef = useRef<number>(equipment.length);

  const hasValidSeason = !!peakSeason && peakSeason !== 'Unknown';

  // Auto-fill when peak season becomes available or changes
  useEffect(() => {
    if (!hasValidSeason) return;
    if (peakSeason === prevSeasonRef.current) return;
    prevSeasonRef.current = peakSeason;

    const overrides = new Set<string>();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    equipment.forEach((eq) => {
      if (eq.equipment_type === '') return;
      const state = getConcurrencyState(eq.equipment_type, peakSeason!);
      if (isAutoFillable(eq, peakSeason!)) {
        const { isConcurrent, reason } = getAutoFillValues(eq.equipment_type, peakSeason!);
        onUpdate(eq.id, { is_concurrent: isConcurrent, concurrency_reason: reason });
      } else if (state !== 'SHOULDER') {
        overrides.add(eq.id);
      }
    });
    setOverrideMode(overrides);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [peakSeason]);

  // Auto-fill newly added equipment rows
  useEffect(() => {
    if (!hasValidSeason) return;
    if (equipment.length <= prevEquipmentLengthRef.current) {
      prevEquipmentLengthRef.current = equipment.length;
      return;
    }
    prevEquipmentLengthRef.current = equipment.length;
    equipment.forEach((eq) => {
      if (eq.concurrency_reason === null && eq.equipment_type !== '') {
        const { isConcurrent, reason } = getAutoFillValues(eq.equipment_type, peakSeason!);
        onUpdate(eq.id, { is_concurrent: isConcurrent, concurrency_reason: reason });
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipment.length]);

  const handleTypeChange = useCallback(
    (id: string, newType: EquipmentType | '') => {
      const eq = equipment.find((e) => e.id === id);
      if (!eq) { onUpdate(id, { equipment_type: newType }); return; }
      const updates: Partial<Equipment> = { equipment_type: newType };
      if (hasValidSeason && newType !== '') {
        const { isConcurrent, reason } = getAutoFillValues(newType, peakSeason!);
        updates.is_concurrent = isConcurrent;
        updates.concurrency_reason = reason;
        setOverrideMode((prev) => { const s = new Set(prev); s.delete(id); return s; });
      }
      onUpdate(id, updates);
    },
    [equipment, hasValidSeason, peakSeason, onUpdate]
  );

  const handleOverride = useCallback((id: string) => {
    setOverrideMode((prev) => new Set([...prev, id]));
  }, []);

  const handleResetToAuto = useCallback(
    (id: string) => {
      const eq = equipment.find((e) => e.id === id);
      if (!eq || !hasValidSeason) return;
      const { isConcurrent, reason } = getAutoFillValues(eq.equipment_type, peakSeason!);
      onUpdate(id, { is_concurrent: isConcurrent, concurrency_reason: reason });
      setOverrideMode((prev) => { const s = new Set(prev); s.delete(id); return s; });
    },
    [equipment, hasValidSeason, peakSeason, onUpdate]
  );

  return (
    <div data-testid={tid('equipment-list')} className="space-y-4">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 flex items-center">
            {isRemoved ? 'Equipment Being Removed' : 'New Loads'}
            <Tooltip text={isRemoved
              ? 'List equipment that is being taken out as part of this work, such as the old A/C a heat pump replaces. Enter its nameplate rating. How it is treated depends on the method and code edition; the note below says how for this project.'
              : 'List every piece of new electrical equipment being installed. Enter each item as a separate row — use the Add Equipment button to add rows. For heat pumps with auxiliary strip heat, enter the compressor and strip heat as separate rows.'} />
          </h2>
          <button
            onClick={() => onAdd()}
            disabled={isReadOnly}
            data-testid={tid('btn-add-equipment')}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
          >
            <Plus size={20} />
            {isRemoved ? 'Add Removed Equipment' : 'Add Equipment'}
          </button>
        </div>

        {isRemoved && removedNote && (
          <p data-testid="removed-treatment-note" className="mb-4 text-xs sm:text-sm text-gray-700">
            {removedNote}
          </p>
        )}

        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
          <p className="text-xs sm:text-sm text-amber-900">
            {hasValidSeason ? (
              <>
                Peak demand was in <strong data-testid={tid('peak-season-word')}>{peakSeason}</strong>.
                {isRemoved
                  ? ' Whether each item ran in that season is set automatically per row; use Override where this house differs.'
                  : ' Concurrency is set automatically per row; use Override where this installation differs.'}
              </>
            ) : (
              <>
                {isRemoved
                  ? 'Whether each item ran in the peak season is set automatically once interval data is loaded on the Existing Load tab.'
                  : `Concurrency (NEC ${measuredCitation}) is set automatically once interval data is loaded on the Existing Load tab.`}
              </>
            )}
          </p>
        </div>

        {recent.length > 0 && !isReadOnly && !isRemoved && (
          <div className="mb-4 flex flex-wrap items-center gap-2" data-testid={tid('equipment-quick-add')}>
            <span className="text-xs sm:text-sm text-gray-600">Add again:</span>
            {recent.map((item, i) => (
              <span
                key={item.key}
                className="inline-flex items-stretch rounded-full border border-gray-300 bg-white text-xs sm:text-sm text-gray-800 hover:border-blue-400 transition-colors"
              >
                <button
                  type="button"
                  onClick={() => onAdd(item.seed)}
                  data-testid={tid(`equipment-quick-add-${i}`)}
                  title="Add a row with this equipment"
                  className={`inline-flex items-center gap-1 py-1.5 hover:bg-blue-50 touch-manipulation ${onRemoveRecent ? 'pl-3 pr-1.5 rounded-l-full' : 'px-3 rounded-full'}`}
                >
                  <Plus size={13} className="text-blue-600" />
                  {item.label}
                </button>
                {onRemoveRecent && (
                  <button
                    type="button"
                    onClick={() => onRemoveRecent(item.key)}
                    data-testid={tid(`equipment-quick-add-remove-${i}`)}
                    title="Remove from Add again"
                    aria-label={`Remove ${item.label} from Add again`}
                    className="inline-flex items-center px-2.5 rounded-r-full text-gray-400 hover:text-red-600 hover:bg-red-50 touch-manipulation"
                  >
                    <X size={13} />
                  </button>
                )}
              </span>
            ))}
          </div>
        )}

        {equipment.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            {isRemoved ? (
              <p className="text-sm">Nothing is being removed. Add a row for any equipment this work takes out.</p>
            ) : (
              <>
                <p>No equipment added yet.</p>
                <p className="text-sm mt-2">
                  Click "Add Equipment" to start building your equipment schedule.
                </p>
              </>
            )}
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="block lg:hidden space-y-4">
              {equipment.map((eq, index) => {
                const state = hasValidSeason
                  ? getConcurrencyState(eq.equipment_type, peakSeason!)
                  : null;
                const isLocked =
                  state !== null && state !== 'SHOULDER' && !overrideMode.has(eq.id) && !isReadOnly;
                // A row with no type selected also falls through to SHOULDER, but there is
                // nothing to review until the preparer says what the load is.
                const isShoulder = state === 'SHOULDER' && eq.equipment_type !== '';
                const isInOverride = overrideMode.has(eq.id);

                return (
                  <div
                    key={eq.id}
                    data-testid={tid(`equipment-row-${index}`)}
                    className="border border-gray-200 rounded-lg p-4 space-y-4 bg-gray-50"
                  >
                    <div className="flex justify-between items-start">
                      <span className="text-sm font-semibold text-gray-700">
                        Equipment #{index + 1}
                      </span>
                      {!isReadOnly && (
                        <div className="flex items-center gap-2">
                          {isRemoved && onReturnToInventory && (
                            <button
                              onClick={() => onReturnToInventory(eq.id)}
                              data-testid={tid(`btn-return-inventory-${index}`)}
                              className="text-xs text-blue-700 underline hover:text-blue-900"
                            >
                              Not removed: back to inventory
                            </button>
                          )}
                          <button
                            onClick={() => onDelete(eq.id)}
                            data-testid={tid(`btn-delete-equipment-${index}`)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors touch-manipulation"
                            title="Delete equipment"
                          >
                            <Trash2 size={20} />
                          </button>
                        </div>
                      )}
                    </div>

                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Equipment Name
                        </label>
                        <input
                          type="text"
                          value={eq.equipment_name}
                          onChange={(e) =>
                            onUpdate(eq.id, { equipment_name: e.target.value })
                          }
                          data-testid={tid(`equipment-name-${index}`)}
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed text-base"
                          disabled={isReadOnly}
                          placeholder="Equipment name"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Type <span className="text-red-600">*</span>
                        </label>
                        <select
                          value={eq.equipment_type}
                          onChange={(e) =>
                            handleTypeChange(eq.id, e.target.value as EquipmentType | '')
                          }
                          data-testid={tid(`equipment-type-${index}`)}
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed text-base"
                          disabled={isReadOnly}
                        >
                          <option value="" disabled>Select type…</option>
                          {EQUIPMENT_TYPES.map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                        </select>
                        {backupHeatQuestion(eq, index, false)}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Source <span className="text-red-600">*</span>{' '}
                          <Tooltip text="Where the rating came from. Nameplate values are preferred — use MCA only when nameplate kW or amp data is unavailable. This is printed on the report as the basis for the rating." />
                        </label>
                        <select
                          value={eq.source ?? ''}
                          onChange={(e) =>
                            onUpdate(eq.id, { source: e.target.value as 'Nameplate' | 'MCA' })
                          }
                          data-testid={tid(`equipment-source-${index}`)}
                          className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed text-base"
                          disabled={isReadOnly}
                        >
                          <option value="" disabled>Select source…</option>
                          <option value="Nameplate">Nameplate</option>
                          <option value="MCA">MCA</option>
                        </select>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Quantity
                          </label>
                          <input
                            type="number"
                            value={eq.quantity}
                            onChange={(e) =>
                              onUpdate(eq.id, { quantity: Number(e.target.value) })
                            }
                            min="1"
                            data-testid={tid(`equipment-quantity-${index}`)}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed text-base"
                            disabled={isReadOnly}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Voltage
                          </label>
                          <input
                            type="number"
                            value={eq.voltage}
                            onChange={(e) =>
                              onUpdate(eq.id, { voltage: Number(e.target.value) })
                            }
                            data-testid={tid(`equipment-voltage-${index}`)}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed text-base"
                            disabled={isReadOnly}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Amps <span className="text-red-600">*</span>{' '}
                            <Tooltip text="Required: enter amps OR kW, not both. Take the value from the equipment nameplate data tag." />
                          </label>
                          <input
                            type="number"
                            value={eq.amps || ''}
                            onChange={(e) =>
                              onUpdate(eq.id, {
                                amps: e.target.value ? Number(e.target.value) : null,
                                kw: e.target.value ? null : eq.kw,
                              })
                            }
                            placeholder="OR"
                            min="0"
                            step="0.1"
                            disabled={isReadOnly}
                            data-testid={tid(`equipment-amps-${index}`)}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed text-base"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            kW <span className="text-red-600">*</span>{' '}
                            <Tooltip text="Required: enter kW OR amps, not both. Take the value from the equipment nameplate data tag." />
                          </label>
                          <input
                            type="number"
                            value={eq.kw || ''}
                            onChange={(e) =>
                              onUpdate(eq.id, {
                                kw: e.target.value ? Number(e.target.value) : null,
                                amps: e.target.value ? null : eq.amps,
                              })
                            }
                            placeholder="OR"
                            min="0"
                            step="0.1"
                            disabled={isReadOnly}
                            data-testid={tid(`equipment-kw-${index}`)}
                            className="w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed text-base"
                          />
                        </div>
                      </div>

                      {/* Concurrent section — mobile */}
                      <div className="space-y-2 py-2">
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            id={`concurrent-${eq.id}`}
                            checked={eq.is_concurrent}
                            onChange={(e) =>
                              onUpdate(eq.id, { is_concurrent: e.target.checked })
                            }
                            data-testid={tid(`equipment-concurrent-${index}`)}
                            disabled={isReadOnly || !hasValidSeason || isLocked}
                            title={
                              !hasValidSeason
                                ? 'Upload AMI data to enable concurrency determination'
                                : isLocked
                                ? 'Auto-determined from peak season. Click Override to modify.'
                                : undefined
                            }
                            className="w-6 h-6 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          />
                          <label
                            htmlFor={`concurrent-${eq.id}`}
                            className="text-sm font-medium text-gray-700"
                          >
                            {isRemoved ? 'Running in the peak season' : 'Concurrent with peak demand'}{' '}
                            {!hasValidSeason && (
                              <Tooltip text="Upload AMI data to enable automatic concurrency determination based on peak season." />
                            )}
                          </label>
                          {!hasValidSeason && (
                            <span
                              className="inline-flex flex-shrink-0"
                              title="Upload AMI data to enable automatic concurrency determination"
                            >
                              <Info size={14} className="text-gray-400" />
                            </span>
                          )}
                        </div>

                        {eq.concurrency_reason && (
                          <div className={`rounded p-2 ${isInOverride ? 'border border-amber-400 bg-amber-50' : 'bg-gray-100'}`}>
                            {isInOverride && !isReadOnly ? (
                              <textarea
                                value={eq.concurrency_reason}
                                onChange={(e) =>
                                  onUpdate(eq.id, { concurrency_reason: e.target.value })
                                }
                                data-testid={tid(`equipment-concurrency-reason-${index}`)}
                                className="w-full text-xs bg-transparent border-none focus:outline-none resize-none"
                                rows={3}
                              />
                            ) : (
                              <p className="text-xs text-gray-600 italic">{eq.concurrency_reason}</p>
                            )}
                          </div>
                        )}

                        {isShoulder && hasValidSeason && !isReadOnly && (
                          <p className="text-xs text-amber-700 font-medium">
                            ⚠ Seasonal status uncertain — review and confirm
                          </p>
                        )}

                        {isLocked && !isReadOnly && (
                          <button
                            onClick={() => handleOverride(eq.id)}
                            data-testid={tid(`btn-override-${index}`)}
                            className="text-xs text-blue-600 hover:text-blue-800 underline"
                          >
                            Override
                          </button>
                        )}

                        {isInOverride && !isReadOnly && (
                          <button
                            onClick={() => handleResetToAuto(eq.id)}
                            className="text-xs text-green-600 hover:text-green-800 underline"
                          >
                            Reset to Auto
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">
                      Equipment Name
                    </th>
                    <th className="w-40 text-left p-3 text-sm font-semibold text-gray-700">
                      Type <span className="text-red-600">*</span>
                    </th>
                    <th className="w-28 text-left p-3 text-sm font-semibold text-gray-700">
                      Source <span className="text-red-600">*</span>{' '}
                      <Tooltip text="Where the rating came from. Nameplate values are preferred — use MCA only when nameplate kW or amp data is unavailable. This is printed on the report as the basis for the rating." />
                    </th>
                    <th className="w-14 text-left p-3 text-sm font-semibold text-gray-700">
                      Qty
                    </th>
                    <th className="min-w-[68px] text-left p-3 text-sm font-semibold text-gray-700">
                      Amps <span className="text-red-600">*</span>{' '}
                      <Tooltip text="Required: enter amps OR kW, not both. Take the value from the equipment nameplate data tag." />
                    </th>
                    <th className="min-w-[68px] text-left p-3 text-sm font-semibold text-gray-700">
                      kW <span className="text-red-600">*</span>{' '}
                      <Tooltip text="Required: enter kW OR amps, not both. Take the value from the equipment nameplate data tag." />
                    </th>
                    <th className="min-w-[68px] text-left p-3 text-sm font-semibold text-gray-700">
                      Voltage
                    </th>
                    <th className="text-left p-3 text-sm font-semibold text-gray-700">
                      {isRemoved ? 'Ran at peak?' : 'Concurrent?'}{' '}
                      {!hasValidSeason && (
                        <Tooltip text="Upload AMI data to enable automatic concurrency determination based on peak season." />
                      )}
                    </th>
                    <th className="w-12 p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {equipment.map((eq, index) => {
                    const state = hasValidSeason
                      ? getConcurrencyState(eq.equipment_type, peakSeason!)
                      : null;
                    const isLocked =
                      state !== null && state !== 'SHOULDER' && !overrideMode.has(eq.id) && !isReadOnly;
                    // See above: an untyped row has no determination to review yet.
                    const isShoulder = state === 'SHOULDER' && eq.equipment_type !== '';
                    const isInOverride = overrideMode.has(eq.id);

                    return (
                      <tr key={eq.id} data-testid={tid(`equipment-row-${index}`)} className="border-b border-gray-100 align-top">
                        <td className="p-3">
                          <input
                            type="text"
                            value={eq.equipment_name}
                            onChange={(e) =>
                              onUpdate(eq.id, { equipment_name: e.target.value })
                            }
                            data-testid={tid(`equipment-name-${index}`)}
                            className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            disabled={isReadOnly}
                            placeholder="Equipment name"
                          />
                        </td>
                        <td className="p-3">
                          <select
                            value={eq.equipment_type}
                            onChange={(e) =>
                              handleTypeChange(eq.id, e.target.value as EquipmentType | '')
                            }
                            data-testid={tid(`equipment-type-${index}`)}
                            className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            disabled={isReadOnly}
                          >
                            <option value="" disabled>Select type…</option>
                            {EQUIPMENT_TYPES.map((type) => (
                              <option key={type} value={type}>
                                {type}
                              </option>
                            ))}
                          </select>
                          {backupHeatQuestion(eq, index, true)}
                        </td>
                        <td className="p-3">
                          <select
                            value={eq.source ?? ''}
                            onChange={(e) =>
                              onUpdate(eq.id, { source: e.target.value as 'Nameplate' | 'MCA' })
                            }
                            data-testid={tid(`equipment-source-${index}`)}
                            className="w-full px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            disabled={isReadOnly}
                          >
                            <option value="" disabled>Select source…</option>
                            <option value="Nameplate">Nameplate</option>
                            <option value="MCA">MCA</option>
                          </select>
                        </td>
                        <td className="p-3">
                          <input
                            type="number"
                            value={eq.quantity}
                            onChange={(e) =>
                              onUpdate(eq.id, { quantity: Number(e.target.value) })
                            }
                            min="1"
                            style={{ width: '52px', minWidth: '52px' }}
                            data-testid={tid(`equipment-quantity-${index}`)}
                            className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            disabled={isReadOnly}
                          />
                        </td>
                        <td className="p-3">
                          <input
                            type="number"
                            value={eq.amps || ''}
                            onChange={(e) =>
                              onUpdate(eq.id, {
                                amps: e.target.value ? Number(e.target.value) : null,
                                kw: e.target.value ? null : eq.kw,
                              })
                            }
                            placeholder="OR"
                            min="0"
                            step="0.1"
                            disabled={isReadOnly}
                            style={{ width: '68px', minWidth: '68px' }}
                            data-testid={tid(`equipment-amps-${index}`)}
                            className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                          />
                        </td>
                        <td className="p-3">
                          <input
                            type="number"
                            value={eq.kw || ''}
                            onChange={(e) =>
                              onUpdate(eq.id, {
                                kw: e.target.value ? Number(e.target.value) : null,
                                amps: e.target.value ? null : eq.amps,
                              })
                            }
                            placeholder="OR"
                            min="0"
                            step="0.1"
                            disabled={isReadOnly}
                            style={{ width: '68px', minWidth: '68px' }}
                            data-testid={tid(`equipment-kw-${index}`)}
                            className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                          />
                        </td>
                        <td className="p-3">
                          <input
                            type="number"
                            value={eq.voltage}
                            onChange={(e) =>
                              onUpdate(eq.id, { voltage: Number(e.target.value) })
                            }
                            style={{ width: '68px', minWidth: '68px' }}
                            data-testid={tid(`equipment-voltage-${index}`)}
                            className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            disabled={isReadOnly}
                          />
                        </td>
                        <td className="p-3">
                          <div className="flex flex-col gap-1.5 min-w-[180px]">
                            <div className="flex items-center gap-1.5">
                              <input
                                type="checkbox"
                                checked={eq.is_concurrent}
                                onChange={(e) =>
                                  onUpdate(eq.id, { is_concurrent: e.target.checked })
                                }
                                disabled={isReadOnly || !hasValidSeason || isLocked}
                                data-testid={tid(`equipment-concurrent-${index}`)}
                                title={
                                  !hasValidSeason
                                    ? 'Upload AMI data to enable concurrency determination'
                                    : isLocked
                                    ? 'Auto-determined from peak season. Click Override to modify.'
                                    : undefined
                                }
                                className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                              />
                              {!hasValidSeason && (
                                <span
                                  className="inline-flex flex-shrink-0"
                                  title="Upload AMI data to enable automatic concurrency determination"
                                >
                                  <Info size={13} className="text-gray-400" />
                                </span>
                              )}
                            </div>

                            {eq.concurrency_reason && (
                              <div
                                className={`rounded p-1.5 ${
                                  isInOverride
                                    ? 'border border-amber-400 bg-amber-50'
                                    : 'bg-gray-50'
                                }`}
                              >
                                {isInOverride && !isReadOnly ? (
                                  <textarea
                                    value={eq.concurrency_reason}
                                    onChange={(e) =>
                                      onUpdate(eq.id, { concurrency_reason: e.target.value })
                                    }
                                    data-testid={tid(`equipment-concurrency-reason-${index}`)}
                                    className="w-full text-xs bg-transparent border-none focus:outline-none resize-none leading-relaxed"
                                    rows={3}
                                  />
                                ) : (
                                  <p className="text-xs text-gray-500 italic leading-relaxed">
                                    {eq.concurrency_reason}
                                  </p>
                                )}
                              </div>
                            )}

                            {isShoulder && hasValidSeason && !isReadOnly && (
                              <p className="text-xs text-amber-700 font-medium">
                                ⚠ Review required
                              </p>
                            )}

                            {isLocked && !isReadOnly && (
                              <button
                                onClick={() => handleOverride(eq.id)}
                                data-testid={tid(`btn-override-${index}`)}
                                className="text-xs text-blue-600 hover:text-blue-800 underline text-left"
                              >
                                Override
                              </button>
                            )}

                            {isInOverride && !isReadOnly && (
                              <button
                                onClick={() => handleResetToAuto(eq.id)}
                                className="text-xs text-green-600 hover:text-green-800 underline text-left"
                              >
                                Reset to Auto
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="p-3">
                          {!isReadOnly && (
                            <div className="flex flex-col items-center gap-1">
                              <button
                                onClick={() => onDelete(eq.id)}
                                data-testid={tid(`btn-delete-equipment-${index}`)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="Delete equipment"
                              >
                                <Trash2 size={18} />
                              </button>
                              {isRemoved && onReturnToInventory && (
                                <button
                                  onClick={() => onReturnToInventory(eq.id)}
                                  data-testid={tid(`btn-return-inventory-${index}`)}
                                  className="text-xs text-blue-700 underline hover:text-blue-900 whitespace-nowrap"
                                  title="Not being removed — put it back in the dwelling inventory"
                                >
                                  Undo
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

      </div>
    </div>
  );
}
